import {
  DrawerContent,
  DrawerHeader,
  SideDrawerFormProps,
  withSideDrawer,
} from '@components/ui-popup/drawers';
import { useAppDispatch } from '@store/store';
import { setNotificationsOpen } from '@store/notifications/notifications-slice';
import {
  useDeleteAllNotificationsMutation,
  useDeleteNotificationsMutation,
  useGetNotificationsQuery,
  useMarkNotificationsAsReadMutation,
} from '@api/endpoints/notification.api';
import { InlineBoxSkeleton } from '@components/skeletons';
import { Box, Button, styled } from '@mui/material';
import Column from '@components/layout-util-components/column';
import { useEffect, useMemo } from 'react';
import useLoadMore from '@hooks/use-load-more-hook';
import Row from '@components/layout-util-components/row';
import { Gap } from '@components/layout-util-components/gap';
import NotificationEntry from '@components/notifications/notification-entry';
import ListContainerSkeleton from '@components/skeletons/list-container-skeleton';
import { LinkButton } from '@components/buttons';
import OnboardingEntryContainer from '@components/containers/onboarding-entry-container';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/pro-light-svg-icons';
import { useUiPopup } from '@components/ui-popup/ui-popup-provider';
import FetchingIndicator from '@components/loading-indicator/fetching-indicator';
import useHideConflictingPanels, {
  SingleUsePanel,
} from '@hooks/rca/use-hide-conflicting-panels-hook';

interface Props extends SideDrawerFormProps<never> {}

const Container = styled(Column)(({ theme: { palette } }) => ({
  zIndex: 100,
  cursor: 'default',

  '.counter': {
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    backgroundColor: palette.primary.main,
    width: '1.25rem',
    height: '1.25rem',
    fontSize: '0.9rem',
    borderRadius: '50%',
    color: 'white',
  },
}));

function NotificationsSidePanel({ show }: Props) {
  const dispatch = useAppDispatch();
  const { showConfirmationModal } = useUiPopup();

  const [markNotificationsAsRead] = useMarkNotificationsAsReadMutation();
  const [deleteAllNotifications] = useDeleteAllNotificationsMutation();
  const [deleteNotifications, { isLoading: isDeleting }] =
    useDeleteNotificationsMutation();

  useHideConflictingPanels(SingleUsePanel.notifications, show);

  const displayFetchingIndicator = isDeleting;

  const { skip, take, canLoadMore, loadMore } = useLoadMore(25);
  const { data, isLoading } = useGetNotificationsQuery(
    {
      skip,
      take,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const unreadCount = useMemo(() => {
    if (data == null) {
      return 0;
    }

    return data.model.filter((n) => !n.read).length;
  }, [data]);

  const unreadIds = useMemo(() => {
    if (data == null) {
      return [];
    }

    return data.model.filter((n) => !n.read).map((x) => x.notificationId);
  }, [data]);

  useEffect(() => {
    if (!show) {
      return;
    }

    if (unreadIds.length > 0) {
      setTimeout(() => {
        markNotificationsAsRead({ notificationIds: unreadIds })
          .unwrap()
          .catch((e) => console.log('mark notifications as read error: ', e));
      }, 3000);
    }
  }, [markNotificationsAsRead, show, unreadIds]);

  const deleteNotification = (notificationId: number) => {
    deleteNotifications({ notificationIds: [notificationId] })
      .unwrap()
      .catch((e) => console.log('delete notifications as read error: ', e));
  };

  const deleteAllNotificationForUser = async () => {
    await showConfirmationModal({
      title: 'Clear all notifications',
      message: 'Are you sure you would like to clear all notifications?',
      yesButtonLabel: 'Yes',
      noButtonLabel: 'No',
      yesButtonAsyncAction: async () => {
        return deleteAllNotifications()
          .unwrap()
          .then(() => true)
          .catch((e) => {
            console.log('delete notifications as read error: ', e);
            return false;
          });
      },
    });
  };

  return (
    <Container>
      <DrawerHeader
        onClose={(_) => {
          dispatch(setNotificationsOpen(false));
        }}
      >
        <Row>
          <span>Notifications</span> <Gap size={15} />
          <span>
            {isLoading ? (
              <InlineBoxSkeleton width={20} />
            ) : unreadCount > 0 ? (
              <div className="counter">{unreadCount}</div>
            ) : null}
          </span>
          {!isLoading && data!.model && data!.model.length > 0 && (
            <Button onClick={deleteAllNotificationForUser}>Clear All</Button>
          )}
          <Gap size={8} />
          <FetchingIndicator show={displayFetchingIndicator} />
        </Row>
      </DrawerHeader>
      <DrawerContent>
        {isLoading ? (
          <ListContainerSkeleton count={5} />
        ) : data!.totalCount === 0 ? (
          <>
            <Gap size={50} />
            <OnboardingEntryContainer
              icon={<FontAwesomeIcon icon={faBell} size="2x" />}
              title="No notifications"
              message="You currently have no notifications"
              hideBgImage
            />
          </>
        ) : (
          <>
            {data!.model.map((notification) => (
              <NotificationEntry
                key={notification.notificationId}
                notification={notification}
                deleteNotification={deleteNotification}
              />
            ))}
            <Gap size={20} />
            <Box alignSelf="center">
              {canLoadMore(data!.totalCount) && (
                <LinkButton onClick={loadMore}>Load more</LinkButton>
              )}
            </Box>
            <Gap size={20} />
          </>
        )}
        <Gap size={50} />
      </DrawerContent>
    </Container>
  );
}

export default withSideDrawer(NotificationsSidePanel, {
  allowTapOutside: true,
});
